import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BellaAssistant } from "@/components/BellaAssistant";
import { Toaster } from "sonner";

import Nav from "@/components/Nav";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import NewOpening from "@/pages/NewOpening";
import RequestStatus from "@/pages/RequestStatus";
import MyRequests from "@/pages/MyRequests";
import Integrations from "@/pages/Integrations";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Marketing from "@/pages/Marketing";
import SubscribePaywall from "@/pages/SubscribePaywall";
import { SubscribeSuccess, SubscribeCancel } from "@/pages/SubscribeResult";
import Pricing from "@/pages/Pricing";
import Download from "@/pages/Download";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Footer from "@/components/Footer";

// Stable Toaster options — extracted so React doesn't re-render Toaster on every parent render.
const TOASTER_OPTIONS = {
  style: {
    borderRadius: 0,
    border: "1px solid #111",
    fontFamily: "IBM Plex Sans",
  },
};

function BusinessRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="mono p-8 text-xs text-muted-foreground">Loading…</div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "business") return <Navigate to="/" replace />;
  return children;
}

function AuthedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="mono p-8 text-xs text-muted-foreground">Loading…</div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="mono p-8 text-xs text-muted-foreground">Loading…</div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin)
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <p
          data-testid="admin-forbidden"
          className="mono text-[10px] uppercase tracking-[0.3em] text-[#F87171]"
        >
          403 — admin only
        </p>
        <h1 className="mt-3 text-3xl leading-tight tracking-tight">Not for you.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This page is restricted to the platform administrator.
        </p>
      </div>
    );
  return children;
}

function AppShell() {
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="App">
        <Nav />
        <BellaAssistant />
        <main id="main-content" tabIndex={-1}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <BusinessRoute>
                <Dashboard />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/new"
            element={
              <BusinessRoute>
                <NewOpening />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/integrations"
            element={
              <BusinessRoute>
                <Integrations />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/profile"
            element={
              <BusinessRoute>
                <Profile />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/marketing"
            element={
              <BusinessRoute>
                <Marketing />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/subscribe"
            element={
              <BusinessRoute>
                <SubscribePaywall expired={false} />
              </BusinessRoute>
            }
          />          <Route
            path="/dashboard/subscription/success"
            element={
              <BusinessRoute>
                <SubscribeSuccess />
              </BusinessRoute>
            }
          />
          <Route
            path="/dashboard/subscription/cancel"
            element={
              <BusinessRoute>
                <SubscribeCancel />
              </BusinessRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            }
          />
          <Route path="/status/:id" element={<RequestStatus />} />
          <Route
            path="/my-requests"
            element={
              <AuthedRoute>
                <MyRequests />
              </AuthedRoute>
            }
          />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/download" element={<Download />} />
        </Routes>
        </main>
        <Footer />
        <Toaster
          position="top-right"
          duration={2500}
          toastOptions={TOASTER_OPTIONS}
        />
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
